import React, { useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  LayoutDashboard,
  Search,
  Filter,
  Calendar,
  Users,
  Building2,
  Globe,
  Target,
  BarChart3,
  TrendingUp,
  Settings,
  Bookmark,
  Clock,
  Bell,
  HelpCircle,
  Wrench,
  FileText,
  Zap,
  Tag,
  Layers,
  Monitor,
  Smartphone,
  Tablet,
  Star,
  PieChart,
  Key,
  Activity,
  Database,
  StickyNote,
  Plus,
  X,
  Bot
} from 'lucide-react';
import googleAdsLogo from '../logos/google-ads.png';
import microsoftAdsLogo from '../logos/microsoft-ads.png';
import metaAdsLogo from '../logos/meta-ads.png';
import './LayoutMockup.css';

/**
 * MAQUETTE UX - Nouvelle interface Kokpeet
 *
 * Principes UX appliqués :
 * 1. Full-width layout (100% écran)
 * 2. Sidebar gauche persistante avec navigation contextuelle
 * 3. Hiérarchie claire : Plateforme > Client > Compte > Filtres
 * 4. Filtres contextuels selon la plateforme active
 * 5. Raccourcis et vues sauvegardées pour les power users
 * 6. Sidebar collapsible pour maximiser l'espace de données
 */

function LayoutMockup() {
  // États de la sidebar
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeSection, setActiveSection] = useState('platforms');

  // États de navigation
  const [selectedPlatform, setSelectedPlatform] = useState('google-ads');
  const [selectedClient, setSelectedClient] = useState('client-1');
  const [selectedAccount, setSelectedAccount] = useState('account-1');

  // États des filtres (expandable sections)
  const [expandedSections, setExpandedSections] = useState({
    platforms: true,
    clients: true,
    filters: true,
    savedViews: false,
    quickActions: false
  });

  // États des filtres sélectionnés
  const [selectedPeriod, setSelectedPeriod] = useState('LAST_30_DAYS');
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedCampaignType, setSelectedCampaignType] = useState(null);
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [selectedNetwork, setSelectedNetwork] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);

  // Données simulées
  const platforms = [
    { id: 'google-ads', name: 'Google Ads', logo: googleAdsLogo, accounts: 12, color: '#4285f4' },
    { id: 'microsoft-ads', name: 'Microsoft Ads', logo: microsoftAdsLogo, accounts: 5, color: '#00a4ef' },
    { id: 'facebook-ads', name: 'Meta Ads', logo: metaAdsLogo, accounts: 8, color: '#1877f2' }
  ];

  const clients = [
    { id: 'client-1', name: 'Acme Corporation', accounts: 4 },
    { id: 'client-2', name: 'Tech Startup SAS', accounts: 2 },
    { id: 'client-3', name: 'E-commerce Plus', accounts: 6 },
    { id: 'client-4', name: 'Retail Group', accounts: 3 }
  ];

  const accounts = [
    { id: 'account-1', name: 'FR - Brand', budget: '€15,000' },
    { id: 'account-2', name: 'FR - Performance', budget: '€25,000' },
    { id: 'account-3', name: 'BE - All Campaigns', budget: '€8,000' }
  ];

  const campaigns = [
    { id: 'camp-1', name: 'Search - Brand FR', type: 'SEARCH' },
    { id: 'camp-2', name: 'Shopping - Products', type: 'SHOPPING' },
    { id: 'camp-3', name: 'PMax - All Products', type: 'PERFORMANCE_MAX' },
    { id: 'camp-4', name: 'Display - Retargeting', type: 'DISPLAY' }
  ];

  const campaignTypes = [
    { id: 'SEARCH', name: 'Search', icon: Search },
    { id: 'SHOPPING', name: 'Shopping', icon: Tag },
    { id: 'PERFORMANCE_MAX', name: 'Performance Max', icon: Zap },
    { id: 'DISPLAY', name: 'Display', icon: Monitor },
    { id: 'VIDEO', name: 'Video', icon: Activity }
  ];

  const labels = [
    { id: 'label-1', name: 'Black Friday', color: '#ef4444' },
    { id: 'label-2', name: 'Evergreen', color: '#22c55e' },
    { id: 'label-3', name: 'Test', color: '#f59e0b' },
    { id: 'label-4', name: 'Top Performers', color: '#8b5cf6' }
  ];

  const savedViews = [
    { id: 'view-1', name: 'Vue Performance Hebdo', platform: 'google-ads' },
    { id: 'view-2', name: 'Analyse Shopping', platform: 'google-ads' },
    { id: 'view-3', name: 'Rapport Mensuel', platform: 'all' }
  ];

  const periods = [
    { id: 'TODAY', name: "Aujourd'hui" },
    { id: 'YESTERDAY', name: 'Hier' },
    { id: 'LAST_7_DAYS', name: '7 derniers jours' },
    { id: 'LAST_14_DAYS', name: '14 derniers jours' },
    { id: 'LAST_30_DAYS', name: '30 derniers jours' },
    { id: 'THIS_MONTH', name: 'Ce mois' },
    { id: 'LAST_MONTH', name: 'Mois dernier' },
    { id: 'LAST_90_DAYS', name: '90 derniers jours' },
    { id: 'CUSTOM', name: 'Personnalisé' }
  ];

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getPlatformFilters = () => {
    // Filtres spécifiques selon la plateforme
    switch (selectedPlatform) {
      case 'google-ads':
        return (
          <>
            {/* Type de campagne */}
            <div className="filter-group">
              <label className="filter-label">
                <Layers size={14} />
                Type de campagne
              </label>
              <div className="filter-chips">
                {campaignTypes.map(type => (
                  <button
                    key={type.id}
                    className={`filter-chip ${selectedCampaignType === type.id ? 'active' : ''}`}
                    onClick={() => setSelectedCampaignType(selectedCampaignType === type.id ? null : type.id)}
                  >
                    <type.icon size={12} />
                    {type.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Réseau */}
            <div className="filter-group">
              <label className="filter-label">
                <Globe size={14} />
                Réseau
              </label>
              <div className="filter-chips">
                {['Search', 'Search Partners', 'Display', 'YouTube'].map(network => (
                  <button
                    key={network}
                    className={`filter-chip ${selectedNetwork === network ? 'active' : ''}`}
                    onClick={() => setSelectedNetwork(selectedNetwork === network ? null : network)}
                  >
                    {network}
                  </button>
                ))}
              </div>
            </div>

            {/* Appareil */}
            <div className="filter-group">
              <label className="filter-label">
                <Monitor size={14} />
                Appareil
              </label>
              <div className="filter-chips">
                <button
                  className={`filter-chip ${selectedDevice === 'desktop' ? 'active' : ''}`}
                  onClick={() => setSelectedDevice(selectedDevice === 'desktop' ? null : 'desktop')}
                >
                  <Monitor size={12} /> Desktop
                </button>
                <button
                  className={`filter-chip ${selectedDevice === 'mobile' ? 'active' : ''}`}
                  onClick={() => setSelectedDevice(selectedDevice === 'mobile' ? null : 'mobile')}
                >
                  <Smartphone size={12} /> Mobile
                </button>
                <button
                  className={`filter-chip ${selectedDevice === 'tablet' ? 'active' : ''}`}
                  onClick={() => setSelectedDevice(selectedDevice === 'tablet' ? null : 'tablet')}
                >
                  <Tablet size={12} /> Tablet
                </button>
              </div>
            </div>
          </>
        );
      case 'microsoft-ads':
        return (
          <>
            <div className="filter-group">
              <label className="filter-label">
                <Layers size={14} />
                Type de campagne
              </label>
              <div className="filter-chips">
                {['Search', 'Shopping', 'Audience', 'Performance Max'].map(type => (
                  <button
                    key={type}
                    className={`filter-chip ${selectedCampaignType === type ? 'active' : ''}`}
                    onClick={() => setSelectedCampaignType(selectedCampaignType === type ? null : type)}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            <div className="filter-group">
              <label className="filter-label">
                <Globe size={14} />
                Réseau
              </label>
              <div className="filter-chips">
                {['Bing', 'AOL', 'Yahoo', 'Syndicated Partners'].map(network => (
                  <button
                    key={network}
                    className={`filter-chip ${selectedNetwork === network ? 'active' : ''}`}
                    onClick={() => setSelectedNetwork(selectedNetwork === network ? null : network)}
                  >
                    {network}
                  </button>
                ))}
              </div>
            </div>
          </>
        );
      case 'facebook-ads':
        return (
          <>
            <div className="filter-group">
              <label className="filter-label">
                <Target size={14} />
                Objectif
              </label>
              <div className="filter-chips">
                {['Conversions', 'Traffic', 'Engagement', 'Awareness', 'Leads'].map(objective => (
                  <button
                    key={objective}
                    className={`filter-chip ${selectedCampaignType === objective ? 'active' : ''}`}
                    onClick={() => setSelectedCampaignType(selectedCampaignType === objective ? null : objective)}
                  >
                    {objective}
                  </button>
                ))}
              </div>
            </div>

            <div className="filter-group">
              <label className="filter-label">
                <Monitor size={14} />
                Placement
              </label>
              <div className="filter-chips">
                {['Facebook', 'Instagram', 'Messenger', 'Audience Network'].map(placement => (
                  <button
                    key={placement}
                    className={`filter-chip ${selectedNetwork === placement ? 'active' : ''}`}
                    onClick={() => setSelectedNetwork(selectedNetwork === placement ? null : placement)}
                  >
                    {placement}
                  </button>
                ))}
              </div>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (selectedCampaign) count++;
    if (selectedCampaignType) count++;
    if (selectedLabels.length > 0) count += selectedLabels.length;
    if (selectedNetwork) count++;
    if (selectedDevice) count++;
    return count;
  };

  const clearAllFilters = () => {
    setSelectedCampaign(null);
    setSelectedCampaignType(null);
    setSelectedLabels([]);
    setSelectedNetwork(null);
    setSelectedDevice(null);
  };

  return (
    <div className="mockup-container">
      {/* HEADER */}
      <header className="mockup-header">
        <div className="mockup-header-content">
          {/* Logo à gauche */}
          <div className="mockup-logo">
            <img src="/kokpit-logo.png" alt="Kokpit" style={{ height: '38px' }} />
            <span className="mockup-badge">MAQUETTE</span>
          </div>

          {/* Groupe central : Clients + Navigation */}
          <div className="header-center-group">
            {/* Sélecteur de client */}
            <div className="header-client-selector">
              <button className="client-selector-btn">
                <Users size={18} />
                <span>Tous les clients</span>
                <ChevronDown size={16} />
              </button>
            </div>

            {/* Navigation */}
            <nav className="mockup-nav">
              <button className="mockup-nav-btn active">
                <LayoutDashboard size={18} />
                Dashboard
              </button>
              <button className="mockup-nav-btn">
                <Bot size={18} />
                Analyser
              </button>
              <button className="mockup-nav-btn">
                <Wrench size={18} />
                Outils
              </button>
              <button className="mockup-nav-btn">
                <StickyNote size={18} />
                Notes
              </button>
            </nav>
          </div>

          {/* User section à droite */}
          <div className="mockup-user">
            <button className="mockup-icon-btn">
              <Bell size={18} />
              <span className="notification-badge">3</span>
            </button>
            <button className="mockup-icon-btn">
              <HelpCircle size={18} />
            </button>
            <div className="mockup-avatar">
              <img src="https://ui-avatars.com/api/?name=User&background=667eea&color=fff" alt="User" />
            </div>
          </div>
        </div>
      </header>

      {/* MAIN LAYOUT */}
      <div className="mockup-layout">
        {/* SIDEBAR GAUCHE */}
        <aside className={`mockup-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          {/* Toggle collapse */}
          <button
            className="sidebar-toggle"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>

          {!sidebarCollapsed && (
            <div className="sidebar-content">
              {/* SECTION: Période */}
              <div className="sidebar-section">
                <div className="section-header static">
                  <span className="section-title">
                    <Calendar size={16} />
                    Période
                  </span>
                </div>
                <div className="section-content">
                  <select
                    className="period-select"
                    value={selectedPeriod}
                    onChange={(e) => setSelectedPeriod(e.target.value)}
                  >
                    {periods.map(period => (
                      <option key={period.id} value={period.id}>{period.name}</option>
                    ))}
                  </select>

                  {selectedPeriod === 'CUSTOM' && (
                    <div className="custom-dates">
                      <input type="date" className="date-input" />
                      <span className="date-separator">→</span>
                      <input type="date" className="date-input" />
                    </div>
                  )}
                </div>
              </div>

              {/* SECTION: Plateformes */}
              <div className="sidebar-section">
                <button
                  className="section-header"
                  onClick={() => toggleSection('platforms')}
                >
                  <span className="section-title">
                    <Database size={16} />
                    Plateformes
                  </span>
                  {expandedSections.platforms ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>

                {expandedSections.platforms && (
                  <div className="section-content">
                    {platforms.map(platform => (
                      <button
                        key={platform.id}
                        className={`platform-item ${selectedPlatform === platform.id ? 'active' : ''}`}
                        onClick={() => setSelectedPlatform(platform.id)}
                        style={{ '--platform-color': platform.color }}
                      >
                        <img src={platform.logo} alt={platform.name} className="platform-logo" />
                        <span className="platform-name">{platform.name}</span>
                        <span className="platform-count">{platform.accounts}</span>
                      </button>
                    ))}

                    {/* Vue multi-plateforme */}
                    <button
                      className={`platform-item multi ${selectedPlatform === 'all' ? 'active' : ''}`}
                      onClick={() => setSelectedPlatform('all')}
                    >
                      <div className="multi-platform-icon">
                        <PieChart size={16} />
                      </div>
                      <span className="platform-name">Vue consolidée</span>
                    </button>
                  </div>
                )}
              </div>

              {/* SECTION: Clients & Comptes */}
              <div className="sidebar-section">
                <button
                  className="section-header"
                  onClick={() => toggleSection('clients')}
                >
                  <span className="section-title">
                    <Building2 size={16} />
                    Clients & Comptes
                  </span>
                  {expandedSections.clients ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>

                {expandedSections.clients && (
                  <div className="section-content">
                    {/* Recherche rapide */}
                    <div className="sidebar-search">
                      <Search size={14} />
                      <input
                        type="text"
                        placeholder="Rechercher..."
                      />
                    </div>

                    {/* Liste des clients avec comptes imbriqués */}
                    <div className="clients-tree">
                      {clients.map(client => (
                        <div key={client.id} className="client-node">
                          <button
                            className={`client-item ${selectedClient === client.id ? 'active' : ''}`}
                            onClick={() => setSelectedClient(client.id)}
                          >
                            <Users size={14} />
                            <span className="client-name">{client.name}</span>
                            <span className="client-count">{client.accounts}</span>
                          </button>

                          {selectedClient === client.id && (
                            <div className="accounts-list">
                              {accounts.map(account => (
                                <button
                                  key={account.id}
                                  className={`account-item ${selectedAccount === account.id ? 'active' : ''}`}
                                  onClick={() => setSelectedAccount(account.id)}
                                >
                                  <span className="account-name">{account.name}</span>
                                  <span className="account-budget">{account.budget}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* SECTION: Filtres */}
              <div className="sidebar-section">
                <button
                  className="section-header"
                  onClick={() => toggleSection('filters')}
                >
                  <span className="section-title">
                    <Filter size={16} />
                    Filtres
                    {getActiveFiltersCount() > 0 && (
                      <span className="filters-badge">{getActiveFiltersCount()}</span>
                    )}
                  </span>
                  {expandedSections.filters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>

                {expandedSections.filters && (
                  <div className="section-content filters-content">
                    {/* Bouton clear all */}
                    {getActiveFiltersCount() > 0 && (
                      <button className="clear-filters-btn" onClick={clearAllFilters}>
                        <X size={12} />
                        Effacer les filtres ({getActiveFiltersCount()})
                      </button>
                    )}

                    {/* Campagne */}
                    <div className="filter-group">
                      <label className="filter-label">
                        <Target size={14} />
                        Campagne
                      </label>
                      <select
                        className="filter-select"
                        value={selectedCampaign || ''}
                        onChange={(e) => setSelectedCampaign(e.target.value || null)}
                      >
                        <option value="">Toutes les campagnes</option>
                        {campaigns.map(camp => (
                          <option key={camp.id} value={camp.id}>{camp.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Labels */}
                    <div className="filter-group">
                      <label className="filter-label">
                        <Tag size={14} />
                        Labels
                      </label>
                      <div className="labels-selector">
                        {labels.map(label => (
                          <button
                            key={label.id}
                            className={`label-chip ${selectedLabels.includes(label.id) ? 'active' : ''}`}
                            onClick={() => {
                              setSelectedLabels(prev =>
                                prev.includes(label.id)
                                  ? prev.filter(id => id !== label.id)
                                  : [...prev, label.id]
                              );
                            }}
                            style={{ '--label-color': label.color }}
                          >
                            <span className="label-dot"></span>
                            {label.name}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Filtres spécifiques à la plateforme */}
                    {getPlatformFilters()}
                  </div>
                )}
              </div>

              {/* SECTION: Actions rapides */}
              <div className="sidebar-section">
                <button
                  className="section-header"
                  onClick={() => toggleSection('quickActions')}
                >
                  <span className="section-title">
                    <Zap size={16} />
                    Actions rapides
                  </span>
                  {expandedSections.quickActions ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>

                {expandedSections.quickActions && (
                  <div className="section-content">
                    <button className="quick-action-btn">
                      <FileText size={14} />
                      Générer rapport PDF
                    </button>
                    <button className="quick-action-btn">
                      <TrendingUp size={14} />
                      Analyse IA
                    </button>
                    <button className="quick-action-btn">
                      <Clock size={14} />
                      Historique
                    </button>
                    <button className="quick-action-btn">
                      <Settings size={14} />
                      Paramètres du compte
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Version collapsed - icônes seulement */}
          {sidebarCollapsed && (
            <div className="sidebar-collapsed-content">
              <button
                className={`collapsed-btn ${selectedPlatform === 'google-ads' ? 'active' : ''}`}
                onClick={() => setSelectedPlatform('google-ads')}
                title="Google Ads"
              >
                <img src={googleAdsLogo} alt="Google Ads" />
              </button>
              <button
                className={`collapsed-btn ${selectedPlatform === 'microsoft-ads' ? 'active' : ''}`}
                onClick={() => setSelectedPlatform('microsoft-ads')}
                title="Microsoft Ads"
              >
                <img src={microsoftAdsLogo} alt="Microsoft Ads" />
              </button>
              <button
                className={`collapsed-btn ${selectedPlatform === 'facebook-ads' ? 'active' : ''}`}
                onClick={() => setSelectedPlatform('facebook-ads')}
                title="Meta Ads"
              >
                <img src={metaAdsLogo} alt="Meta Ads" />
              </button>
              <div className="collapsed-divider" />
              <button className="collapsed-btn" title="Filtres">
                <Filter size={18} />
                {getActiveFiltersCount() > 0 && (
                  <span className="collapsed-badge">{getActiveFiltersCount()}</span>
                )}
              </button>
              <button className="collapsed-btn" title="Actions rapides">
                <Zap size={18} />
              </button>
            </div>
          )}
        </aside>

        {/* CONTENU PRINCIPAL */}
        <main className="mockup-main">
          {/* Breadcrumb contextuel */}
          <div className="mockup-breadcrumb">
            <span className="breadcrumb-item">
              <img
                src={platforms.find(p => p.id === selectedPlatform)?.logo || googleAdsLogo}
                alt=""
                className="breadcrumb-logo"
              />
              {platforms.find(p => p.id === selectedPlatform)?.name || 'Google Ads'}
            </span>
            <ChevronRight size={14} className="breadcrumb-separator" />
            <span className="breadcrumb-item">
              {clients.find(c => c.id === selectedClient)?.name || 'Client'}
            </span>
            <ChevronRight size={14} className="breadcrumb-separator" />
            <span className="breadcrumb-item active">
              {accounts.find(a => a.id === selectedAccount)?.name || 'Compte'}
            </span>

            {/* Filtres actifs inline */}
            {getActiveFiltersCount() > 0 && (
              <div className="active-filters-inline">
                <span className="filters-separator">|</span>
                {selectedCampaignType && (
                  <span className="active-filter-tag">
                    {selectedCampaignType}
                    <X size={12} onClick={() => setSelectedCampaignType(null)} />
                  </span>
                )}
                {selectedNetwork && (
                  <span className="active-filter-tag">
                    {selectedNetwork}
                    <X size={12} onClick={() => setSelectedNetwork(null)} />
                  </span>
                )}
                {selectedDevice && (
                  <span className="active-filter-tag">
                    {selectedDevice}
                    <X size={12} onClick={() => setSelectedDevice(null)} />
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Zone de contenu simulée */}
          <div className="mockup-content">
            <div className="content-placeholder">
              <div className="placeholder-header">
                <h1><BarChart3 size={24} />&nbsp;Résultats</h1>
                <p>Période : {periods.find(p => p.id === selectedPeriod)?.name}</p>
              </div>

              {/* KPIs simulés */}
              <div className="placeholder-kpis">
                <div className="kpi-card">
                  <span className="kpi-label">Impressions</span>
                  <span className="kpi-value">1,234,567</span>
                  <span className="kpi-change positive">+12.5%</span>
                </div>
                <div className="kpi-card">
                  <span className="kpi-label">Clics</span>
                  <span className="kpi-value">45,678</span>
                  <span className="kpi-change positive">+8.3%</span>
                </div>
                <div className="kpi-card">
                  <span className="kpi-label">CTR</span>
                  <span className="kpi-value">3.70%</span>
                  <span className="kpi-change negative">-0.2%</span>
                </div>
                <div className="kpi-card">
                  <span className="kpi-label">Conversions</span>
                  <span className="kpi-value">1,234</span>
                  <span className="kpi-change positive">+15.7%</span>
                </div>
                <div className="kpi-card">
                  <span className="kpi-label">Coût</span>
                  <span className="kpi-value">€12,345</span>
                  <span className="kpi-change neutral">+2.1%</span>
                </div>
                <div className="kpi-card">
                  <span className="kpi-label">CPA</span>
                  <span className="kpi-value">€10.00</span>
                  <span className="kpi-change positive">-5.3%</span>
                </div>
              </div>

              {/* Graphique simulé */}
              <div className="placeholder-chart">
                <div className="chart-header">
                  <h3>Performance sur la période</h3>
                  <div className="chart-controls">
                    <button className="chart-control active">Jour</button>
                    <button className="chart-control">Semaine</button>
                    <button className="chart-control">Mois</button>
                  </div>
                </div>
                <div className="chart-area">
                  <div className="chart-placeholder-text">
                    Zone du graphique PerformanceChart
                  </div>
                </div>
              </div>

              {/* Sections simulées */}
              <div className="placeholder-sections">
                <div className="section-card">
                  <h3><Target size={16} />&nbsp;Campagnes</h3>
                  <div className="section-placeholder">Tableau des campagnes</div>
                </div>
                <div className="section-card">
                  <h3><Key size={16} />&nbsp;Mots-clés</h3>
                  <div className="section-placeholder">Tableau des mots-clés</div>
                </div>
                <div className="section-card">
                  <h3><Globe size={16} />&nbsp;Géographie</h3>
                  <div className="section-placeholder">Performance par région</div>
                </div>
                <div className="section-card">
                  <h3><Smartphone size={16} />&nbsp;Appareils</h3>
                  <div className="section-placeholder">Répartition par device</div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Footer info maquette */}
      <div className="mockup-footer-info">
        <span>MAQUETTE UX - Cette page est une démonstration du nouveau layout proposé</span>
        <a href="/" className="back-link">← Retour à l'interface actuelle</a>
      </div>
    </div>
  );
}

export default LayoutMockup;
